import { z } from '../../../zod';

export const PageTaxModeValidationSchema = z.enum(['none', 'fixed', 'stripe']);

type TaxModeValue = z.infer<typeof PageTaxModeValidationSchema>;

/**
 * enabled/mode cross-field rule, kept in lockstep with
 * apps/api/src/pages/schemas/taxSchema.ts (same messages and paths) since
 * the API enforces this independently of whatever validated the request.
 */
export function withTaxModeRefinements<
  T extends z.ZodObject<{
    enabled: z.ZodType<boolean>;
    mode: z.ZodType<TaxModeValue | undefined>;
  }>,
>(schema: T) {
  return schema
    .refine(data => !(data.enabled === false && data.mode !== undefined && data.mode !== 'none'), {
      message: 'tax.mode must be none when tax.enabled is false',
      path: ['mode'],
    })
    .refine(
      data =>
        !(
          data.enabled === true &&
          data.mode !== undefined &&
          !['fixed', 'stripe'].includes(data.mode)
        ),
      {
        message: 'tax.mode must be fixed or stripe when tax.enabled is true',
        path: ['mode'],
      },
    );
}

export const CreatePageTaxValidationSchema = withTaxModeRefinements(
  z.object({
    enabled: z.boolean().meta({
      description:
        'If enabled mode must be one of `fixed` or `stripe`. If disabled mode must be `none`.',
    }),
    mode: PageTaxModeValidationSchema.optional().meta({
      description:
        'Use mode `none` to not collect any tax. Use mode `fixed` for a fixed tax rate at checkout (configure the rate ID on `product.fixedTaxRateIds` for checkout pages or `eventDetails.fixedTaxRateIds` for event pages). Use mode `stripe` to enable Stripe tax, make sure Stripe tax is enabled globally in your Checkout Page account and configured in Stripe. You must also configure address fields in your page for the Stripe Tax calculation to be completed successfully. If omitted when `enabled` is `true`, defaults to `stripe`.',
    }),
  }),
).openapi({
  description: 'Tax configuration for this page.',
  example: {
    enabled: true,
    mode: 'fixed',
  },
});

export const PageTaxResponseValidationSchema = z
  .object({
    enabled: z.boolean().nullish().meta({
      description: 'If enabled tax is collected. Method of tax collection depends on `mode`.',
    }),
    mode: PageTaxModeValidationSchema.nullish().meta({
      description:
        'Mode `none` means no tax is collected. `fixed` means tax is collected at a fixed rate defined in the tax rate. `stripe` means Stripe tax is used to calculate tax.',
    }),
  })
  .openapi({
    description: 'Tax configuration for this page.',
  });
